import { VIEW_EARLY_ACCESS_FEATURES } from "@flow/common/core_permissions";
import { whoAmI } from "@flow/common/core_routes";
import { Text } from "@flow/lib/i18n";
import React from "react";
import { getAllRoutes } from "../FrontendMemberRoute";
import { usePostHog } from "posthog-js/react";

/**
 * `MemberNonJourney` is the top level React component for member facing non-journey components
 * To be used for pages that are not part of a member journey.
 * Based on the admin top level component, front end components register themselves with a #fragment route
 * and then this component renders the appropriate component based on the current fragement.
 * For Example:
 * The route /page/#send-a-message/submission-video
 * Renders the component at src/plugins/send_a_message/frontend/member/TargetVideoLandingPage.tsx
 *
 * @param props - The component props:
 *   - `initialRoute`: Identifier used to determing which React component to render initially
 *
 * @returns A `MemberJourney` React component.
 */
const MemberNonJourney = (props: { initialRoute: string; setIsEarlyAccessStaffUser: (_: boolean) => void }) => {
  const posthog = usePostHog();
  const [rawRoute, _setRawRoute] = React.useState<string>(props.initialRoute);

  function setRawRoute(r: string) {
    window.location.hash = r;
    _setRawRoute(r);
  }

  window.addEventListener("hashchange", () => setRawRoute(window.location.hash.slice(1)), false);

  const activeComponent = () => {
    const newprops = {
      rawRoute,
    };
    for (const feRoute of getAllRoutes()) {
      const match = feRoute.match(rawRoute, newprops);
      if (match) return match;
    }
    return (
      <div>
        <Text id="no_page_found">No page was found at this URL</Text>
      </div>
    );
  };

  React.useEffect(() => {
    // TODO: this mechanism for identifying Early Access users is only suitable for 38 Degrees
    //       staff. A different approach will be needed if we want to feature flag this component
    //       for supporter users.
    whoAmI.call().then((user) => {
      props.setIsEarlyAccessStaffUser(user?.permissions.includes(VIEW_EARLY_ACCESS_FEATURES) ?? false);
      if (user !== undefined) {
        // user is only returns as defined for Flow admin users (i.e. staff)
        try {
          // intentionally leaving first arg undefined, see Posthog function type def.
          // is_staff should be set once and is intended not to be overridden.
          posthog?.setPersonProperties(undefined, { is_staff: true });
        } catch (e) {
          console.warn("Failed to identify staff member to Posthog", e);
        }
      }
    });
  }, []);

  return activeComponent();
};

export { MemberNonJourney };
